import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Truck, Printer, Send, CheckCircle2, ClipboardList } from "lucide-react";

const ORDER_STATUS_LABEL = { pending: "Da inviare", sent: "Inviato", delivered: "Consegnato" };
const ORDER_STATUS_CLASS = {
  pending: "bg-amber-100 text-amber-700",
  sent: "bg-blue-100 text-blue-700",
  delivered: "bg-emerald-100 text-emerald-700",
};
const fmtEur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (o, status) => {
    await api.put(`/events/${o.event_id}/orders/${o.order_number}/status`, { status });
    toast.success(`Ordine ${o.order_number}: ${ORDER_STATUS_LABEL[status]}`);
    load();
  };

  const printOrder = (o) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const rows = (o.items || []).map((it) => `<tr><td style="padding:8px;border-bottom:1px solid #eee;text-transform:capitalize">${it.ingredient}</td><td style="padding:8px;text-align:right;border-bottom:1px solid #eee">${it.quantity_kg.toFixed(2)} kg</td><td style="padding:8px;text-align:right;border-bottom:1px solid #eee">€${it.price_per_kg.toFixed(2)}</td><td style="padding:8px;text-align:right;border-bottom:1px solid #eee">€${it.total.toFixed(2)}</td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><title>${o.order_number}</title><style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:0 20px;color:#0F2741}h1{color:#2563EB;border-bottom:2px solid #2563EB;padding-bottom:8px;font-size:32px}table{width:100%;border-collapse:collapse;margin-top:20px}th{padding:8px;text-align:left;background:#EFF6FF;color:#0F2741;border-bottom:2px solid #2563EB}th:nth-child(n+2){text-align:right}.tot{font-size:28px;font-weight:bold;color:#2563EB}</style></head><body>
      <h1>Ordine ${o.order_number}</h1>
      <p><strong>Fornitore:</strong> ${o.supplier_name}<br/><strong>Data ordine:</strong> ${new Date(o.created_at).toLocaleDateString("it-IT")}<br/><strong>Per evento del:</strong> ${o.event_date} · ${o.guests} ospiti (${o.portions} porzioni con margine +5%)</p>
      <table><thead><tr><th>Ingrediente</th><th>Quantità</th><th>€/kg</th><th>Totale</th></tr></thead><tbody>${rows}</tbody></table>
      <p style="text-align:right;margin-top:30px"><span class="tot">Totale: €${o.total.toFixed(2)}</span></p>
      <p style="margin-top:60px;font-size:12px;color:#64748B">Generato da Event System 360 · ${new Date().toLocaleString("it-IT")}</p>
      <script>window.print()</script></body></html>`);
    w.document.close();
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);
  const stats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === "pending").length,
    sent: orders.filter((o) => o.status === "sent").length,
    delivered: orders.filter((o) => o.status === "delivered").length,
    totalValue: orders.reduce((s, o) => s + (o.total || 0), 0),
  };

  return (
    <div className="space-y-8" data-testid="orders-page">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Operazioni</p>
        <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">Ordini ai fornitori</h1>
        <p className="text-slate-500 mt-2">Tutti gli ordini ufficiali emessi dopo approvazione preventivi</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KPI label="Totale ordini" value={stats.total} />
        <KPI label="Da inviare" value={stats.pending} accent="amber" />
        <KPI label="Inviati" value={stats.sent} accent="blue" />
        <KPI label="Consegnati" value={stats.delivered} accent="emerald" />
        <KPI label="Valore totale" value={fmtEur(stats.totalValue)} />
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "pending", "sent", "delivered"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`order-filter-${f}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === f ? "bg-[#0F2741] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#2563EB]"}`}>
            {f === "all" ? "Tutti" : ORDER_STATUS_LABEL[f]}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nessun ordine emesso. Approva un preventivo per generare ordini.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-6 py-3">Numero</th>
                <th className="text-left px-6 py-3">Fornitore</th>
                <th className="text-left px-6 py-3">Evento</th>
                <th className="text-right px-6 py-3">Totale</th>
                <th className="text-center px-6 py-3">Stato</th>
                <th className="text-right px-6 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.order_number} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-6 py-3 font-semibold text-[#2563EB]">{o.order_number}</td>
                  <td className="px-6 py-3 text-[#0F2741]">{o.supplier_name}</td>
                  <td className="px-6 py-3">
                    <button onClick={() => navigate(`/eventi/${o.event_id}`)} className="text-xs text-slate-600 hover:text-[#2563EB] underline-offset-2 hover:underline">
                      {o.event_date} · {o.guests} ospiti
                    </button>
                  </td>
                  <td className="px-6 py-3 text-right font-semibold">{fmtEur(o.total)}</td>
                  <td className="px-6 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${ORDER_STATUS_CLASS[o.status]}`}>{ORDER_STATUS_LABEL[o.status]}</span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => printOrder(o)} title="Stampa" className="p-1.5 text-slate-400 hover:text-[#2563EB]"><Printer className="w-4 h-4" /></button>
                      {o.status === "pending" && (
                        <button onClick={() => updateStatus(o, "sent")} title="Segna come inviato" className="p-1.5 text-slate-400 hover:text-blue-600"><Send className="w-4 h-4" /></button>
                      )}
                      {o.status === "sent" && (
                        <button onClick={() => updateStatus(o, "delivered")} title="Segna consegnato" className="p-1.5 text-slate-400 hover:text-emerald-600"><CheckCircle2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, accent }) {
  const accents = {
    amber: "text-amber-600", blue: "text-blue-600", emerald: "text-emerald-600",
  };
  return (
    <div className="p-5 rounded-xl bg-white border border-slate-200">
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className={`font-display text-2xl mt-2 ${accents[accent] || "text-[#0F2741]"}`}>{value}</p>
    </div>
  );
}
